export interface BoundElement extends HTMLElement {
  checked: boolean;
  close(returnValue?: string): void;
  contentDocument: Document | null;
  contentWindow: Window | null;
  disabled: boolean;
  files: FileList | null;
  href: string;
  indeterminate: boolean;
  open: boolean;
  options: HTMLOptionsCollection;
  placeholder: string;
  requestSubmit(submitter?: HTMLElement | null): void;
  required: boolean;
  select(): void;
  selectedOptions: HTMLCollectionOf<HTMLOptionElement>;
  showModal(): void;
  src: string;
  value: string;
  closest(selectors: string): BoundElement | null;
  querySelector(selectors: string): BoundElement | null;
  querySelectorAll(selectors: string): NodeListOf<BoundElement>;
}

export function mustElement<T extends HTMLElement = BoundElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Required element #${id} is missing`);
  }
  return element as T;
}

export function mustQuery<T extends Element = BoundElement>(selector: string, root: ParentNode = document): T {
  const element = root.querySelector(selector);
  if (!element) {
    throw new Error(`Required element ${selector} is missing`);
  }
  return element as T;
}

export function allElements<T extends Element = BoundElement>(selector: string, root: ParentNode = document): T[] {
  return [...root.querySelectorAll<T>(selector)];
}

export function eventElement(event: Event): BoundElement {
  if (!(event.target instanceof HTMLElement)) {
    throw new Error(`Expected an HTML event target for ${event.type}`);
  }
  return event.target as BoundElement;
}

export function closestElement(event: Event, selector: string): BoundElement | null {
  return eventElement(event).closest(selector);
}

export function queryElement<T extends Element = BoundElement>(selector: string, root: ParentNode = document): T | null {
  return root.querySelector<T>(selector);
}

export function errorMessage(error: unknown, fallback = "Request failed"): string {
  return error instanceof Error && error.message ? error.message : fallback;
}
